// Version 1 output — the tab shell for a scenario's results. It owns the toggle
// between the three views and the display-currency picker, and composes the
// Staff Plan tab's cards. The FTE matrix itself lives in ModeledMatrixCard,
// which the Summary page also mounts, so the two pages cannot drift apart.
//
// Basic scenarios skip modeled sizing: the Staff Plan tab shows the selected
// plan after OGS hub proportion, read-only.

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Coins, LayoutGrid, Sparkles, Users } from 'lucide-react';
import { applyStaffPlanOverrides, staffPlanToOgsAdjustedBasisCells } from '@rc/engine';
import type {
  CalculationResult,
  ClientProfile,
  Model,
  ScenarioWorkspace,
  StaffPlan,
} from '@rc/engine/types';
import HubComparisonCard from './HubComparisonCard';
import CostAnalysisCard from './CostAnalysisCard';
import PhaseConcentrationCard from './PhaseConcentrationCard';
import AutomationImpactView from './AutomationImpactView';
import SummaryView from './SummaryView';
import ModeledMatrixCard from './ModeledMatrixCard';
import type { FindPublishedPlan, PublishPlanArgs } from './publishPlan';
import { useCurrency } from '../../lib/currency';

type ViewMode = 'staff_plan' | 'automation_impact' | 'summary';

interface Props {
  model: Model;
  result: CalculationResult;
  profile: ClientProfile;
  staffPlan?: StaffPlan | null;
  workspace?: ScenarioWorkspace;
  clientId?: string;
  geoclientId?: string;
  onProfileChange?: (patch: Partial<ClientProfile>) => void;
  /** Publish an output matrix to the client workspace as a saved staff plan.
   *  The caller owns naming provenance and refreshing its own plan list. */
  onPublishPlan?: (args: PublishPlanArgs) => Promise<void>;
  /** Resolves which saved plan a matrix would update in place. */
  findPublishedPlan?: FindPublishedPlan;
  /** Seed for the publish name field, e.g. the scenario's name. */
  defaultPlanName?: string;
}

export default function Version1View({
  model,
  result,
  profile,
  staffPlan,
  workspace = 'advanced',
  clientId,
  geoclientId,
  onProfileChange,
  onPublishPlan,
  findPublishedPlan,
  defaultPlanName,
}: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('staff_plan');
  const isBasic = workspace === 'basic';

  // Advanced: modeled grid + manual overrides.
  // Basic: staff plan totals re-split by profile.ogs (scenario overlay).
  const adjusted = useMemo(() => {
    if (isBasic) {
      const cells = staffPlanToOgsAdjustedBasisCells(staffPlan, model, profile);
      return { cells, appliedCount: 0, orphanedKeys: [] as string[], drifted: [] };
    }
    return applyStaffPlanOverrides(
      result.byDisciplineChannelLevel,
      profile.staffPlanOverrides,
      model,
    );
  }, [
    isBasic,
    staffPlan,
    model,
    profile,
    result.byDisciplineChannelLevel,
    profile.staffPlanOverrides,
  ]);

  const basicResult = useMemo(() => {
    if (!isBasic) return result;
    return {
      ...result,
      byDisciplineChannelLevel: adjusted.cells,
    };
  }, [isBasic, result, adjusted.cells]);

  if (viewMode === 'automation_impact') {
    return (
      <div className="space-y-4">
        <ViewHeader value={viewMode} onChange={setViewMode} />
        <AutomationImpactView
          model={model}
          result={result}
          profile={profile}
          staffPlan={staffPlan}
          workspace={workspace}
          clientId={clientId}
          geoclientId={geoclientId}
          onProfileChange={onProfileChange}
        />
      </div>
    );
  }

  if (viewMode === 'summary') {
    return (
      <div className="space-y-4">
        <ViewHeader value={viewMode} onChange={setViewMode} />
        <SummaryView
          model={model}
          result={result}
          profile={profile}
          staffPlan={staffPlan}
          workspace={workspace}
          onProfileChange={isBasic ? undefined : onProfileChange}
          onPublishPlan={onPublishPlan}
          findPublishedPlan={findPublishedPlan}
          defaultPlanName={defaultPlanName}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ViewHeader value={viewMode} onChange={setViewMode} />
      <CostAnalysisCard
        model={model}
        cells={adjusted.cells}
        adjusted={!isBasic && adjusted.appliedCount > 0}
        staffPlan={staffPlan}
      />
      <ModeledMatrixCard
        model={model}
        result={basicResult}
        profile={profile}
        onProfileChange={isBasic ? undefined : onProfileChange}
        onPublishPlan={isBasic ? undefined : onPublishPlan}
        findPublishedPlan={isBasic ? undefined : findPublishedPlan}
        defaultPlanName={defaultPlanName}
        eyebrow={isBasic ? 'Staff plan (after OGS proportion)' : undefined}
        notice={
          isBasic ? (
            <>
              Read-only view of the selected staff plan after this scenario&apos;s
              OGS hub proportion. The saved plan is unchanged.
            </>
          ) : undefined
        }
      />
      <HubComparisonCard
        model={model}
        cells={adjusted.cells}
        adjusted={!isBasic && adjusted.appliedCount > 0}
        staffPlan={staffPlan}
      />
      <PhaseConcentrationCard model={model} cells={adjusted.cells} />
    </div>
  );
}

// Top-of-tab header: the view toggle on the left, and — when the model set up
// more than one currency — a display-currency picker on the right.
function ViewHeader({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <ViewModeToggle value={value} onChange={onChange} />
      <CurrencySelect />
    </div>
  );
}

// Only rendered when the model offers an alternate currency, so a USD-only
// model's output looks exactly as it did before.
function CurrencySelect() {
  const { display, setDisplay, options, base } = useCurrency();
  if (options.length < 2) return null;

  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-500">
      <Coins size={14} className="text-slate-400" />
      <span>Display in</span>
      <select
        className="input py-1 px-2 text-xs w-auto tabular-nums"
        value={display}
        onChange={(e) => setDisplay(e.target.value)}
      >
        {options.map((code) => (
          <option key={code} value={code}>
            {code}
            {code === base ? ' (model)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

// Top-of-tab toggle between the staff-plan analysis cards (cost, FTE table,
// hub comparison, phase concentration) and the automation-impact projection.
function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const items: { id: ViewMode; label: string; icon: typeof Users }[] = [
    { id: 'staff_plan', label: 'Staff Plan', icon: Users },
    { id: 'automation_impact', label: 'Automation Impact', icon: Sparkles },
    { id: 'summary', label: 'Summary', icon: LayoutGrid },
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
      {items.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={clsx(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
            value === id
              ? 'bg-brand-purple-600 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
          )}
        >
          <Icon size={13} />
          {label}
        </button>
      ))}
    </div>
  );
}
